import { TpDataTableColumn } from '../../../data/interfaces';
import { currentTheme } from '../../themes/themes';
import { TpAreaRenderContext } from '../zoompan-canvas/zoomPanCanvasHelpers';
import { TpPresetColorNumerical, getColorFrac, getColorStringIdx } from './colorMappingInfo';
import { color, Color } from './colors';
import { convertColumnDataToDisplayString, getClippedRenderText } from './misc';

export enum LEGEND_ITEM_TYPES {
  CategoricalColors = 'categoricalColors',
  NumericalColors = 'numericalColors',
  PointSize = 'pointSize',
  NamedColorSet = 'namedColorSet',
}

export function renderXLabel(renderContext: TpAreaRenderContext, label: string) {
  const ctx = renderContext.canvasContext;
  renderContext.setFont(10);
  ctx.textAlign = 'right';
  ctx.textBaseline = 'top';
  ctx.fillStyle = renderContext.colorSchema.foreColor.toCanvasString();
  ctx.fillText(label, renderContext.areaXMax, renderContext.areaYMax);
}

const HEADER_BUTTON_WIDTH = 10;

export function renderYLabel(renderContext: TpAreaRenderContext, label: string, title: string) {
  const ctx = renderContext.canvasContext;
  renderContext.setFont(10);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = renderContext.colorSchema.foreColor.toCanvasString();
  ctx.fillText(label, renderContext.areaXMin, renderContext.areaYMax + renderContext.mapsize(23));
  renderContext.setFont(12, false);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillStyle = currentTheme.colors.supportPrim.toCanvasString();
  ctx.fillText(
    getClippedRenderText(
      ctx,
      title,
      renderContext.areaXMax - renderContext.areaXMin - renderContext.mapsize(HEADER_BUTTON_WIDTH),
    ),
    renderContext.areaXMin,
    renderContext.areaYMax,
  );
}

export interface TpLegendCategoricalColors {
  type: LEGEND_ITEM_TYPES.CategoricalColors;
  colorColumnInfo: TpDataTableColumn;
  colorCategories: string[];
  colorCat2Index: { [cat: string]: number };
  colorValueStrings: string[];
}

export interface TpLegendNumericalColors {
  type: LEGEND_ITEM_TYPES.NumericalColors;
  colorColumnInfo: TpDataTableColumn | null;
  customTitle?: string;
  colorValueStrings: string[];
  minValColor: number;
  maxValColor: number;
  gammaFactor: number;
  aggregationMethod?: string;
  numericalLegendItems: { posit: number; label: string }[];
  presetColorsNumerical: TpPresetColorNumerical[] | null;
}

export interface TpLegendSize {
  type: LEGEND_ITEM_TYPES.PointSize;
  sizeColumnInfo: TpDataTableColumn | null;
  customTitle?: string;
  minValPointSize: number;
  maxValPointSize: number;
}

export interface TpLegendNamedColorSet {
  type: LEGEND_ITEM_TYPES.NamedColorSet;
  items: { name: string; color: Color }[];
}

export type TpLegendItem = TpLegendCategoricalColors | TpLegendNumericalColors | TpLegendSize | TpLegendNamedColorSet;

export function renderLegendItems(renderContext: TpAreaRenderContext, legendItems: TpLegendItem[]) {
  const { mapx, mapy, pixelConv, setFont } = renderContext;
  setFont(8);
  const ctx = renderContext.canvasContext;
  ctx.textBaseline = 'top';
  let posX = renderContext.areaXMin;
  let posY = renderContext.areaYMax + pixelConv(8);
  const lineH = pixelConv(12);

  const foreColStr = renderContext.colorSchema.foreColor.toCanvasString();

  function addString(content: string) {
    const itemSize = ctx.measureText(content).width + pixelConv(5);
    if (posX + itemSize > renderContext.areaXMax) {
      posX = renderContext.areaXMin;
      posY += lineH;
    }
    ctx.fillStyle = foreColStr;
    ctx.fillText(content, posX, posY - pixelConv(5));
    posX += itemSize;
  }

  for (const legendItem of legendItems) {
    let handled = false;

    if (legendItem.type == LEGEND_ITEM_TYPES.CategoricalColors) {
      const legendItemCat = legendItem as TpLegendCategoricalColors;
      const colorColumnInfo = legendItemCat.colorColumnInfo;
      const colorValueStrings = legendItemCat.colorValueStrings;
      const colorCategories = legendItemCat.colorCategories;
      const colorCat2Index = legendItemCat.colorCat2Index;
      addString(`${colorColumnInfo.name}:`);
      // Go over the categories in order set in the colorMappingInfo.
      for (const category of colorCategories) {
        const colorIdx = colorCat2Index[category];
        if (colorIdx < colorValueStrings.length) {
          const label = convertColumnDataToDisplayString(colorColumnInfo, category);
          const itemSize = ctx.measureText(label).width + pixelConv(17);
          if (posX + itemSize > renderContext.areaXMax) {
            posX = renderContext.areaXMin;
            posY += lineH;
          }
          ctx.fillStyle = colorValueStrings[colorIdx];
          ctx.beginPath();
          ctx.arc(posX + pixelConv(4), posY, pixelConv(4), 0, 2 * Math.PI);
          ctx.fill();
          ctx.fillStyle = foreColStr;
          ctx.fillText(label, posX + pixelConv(10), posY - pixelConv(5));
          posX += itemSize;
        }
      }
      handled = true;
    }

    if (legendItem.type == LEGEND_ITEM_TYPES.NumericalColors) {
      const legendItemNum = legendItem as TpLegendNumericalColors;
      const colorColumnInfo = legendItemNum.colorColumnInfo;
      const name = '';
      const colorValueStrings = legendItemNum.colorValueStrings;

      if (legendItemNum.customTitle) {
        addString(`${legendItem.customTitle}:`);
      } else {
        let aggregationString = '';
        if (legendItemNum.aggregationMethod) aggregationString = ` (${legendItemNum.aggregationMethod})`;
        if (colorColumnInfo) addString(`${colorColumnInfo.name}${aggregationString}:`);
      }
      if (legendItemNum.presetColorsNumerical) {
        for (const preset of legendItemNum.presetColorsNumerical) {
          if (preset.label) {
            const itemSize = ctx.measureText(preset.label).width + pixelConv(17);
            if (posX + itemSize > renderContext.areaXMax) {
              posX = renderContext.areaXMin;
              posY += lineH;
            }
            ctx.fillStyle = color(preset.color.r, preset.color.g, preset.color.b).toCanvasString();
            ctx.beginPath();
            ctx.arc(posX + pixelConv(4), posY, pixelConv(4), 0, 2 * Math.PI);
            ctx.fill();
            ctx.fillStyle = foreColStr;
            ctx.fillText(preset.label, posX + pixelConv(10), posY - pixelConv(5));
            posX += itemSize;
          }
        }
      }
      for (const axisItem of legendItemNum.numericalLegendItems) {
        if (axisItem.label) {
          const itemSize = ctx.measureText(axisItem.label).width + pixelConv(17);
          if (posX + itemSize > renderContext.areaXMax) {
            posX = renderContext.areaXMin;
            posY += lineH;
          }
          const colorFrac = getColorFrac(
            axisItem.posit,
            legendItemNum.minValColor!,
            legendItemNum.maxValColor!,
            legendItemNum.gammaFactor,
          );
          const colorIdx = getColorStringIdx(colorValueStrings, colorFrac);
          const colorStr = colorValueStrings[colorIdx];

          ctx.fillStyle = colorStr;
          ctx.beginPath();
          ctx.arc(posX + pixelConv(4), posY, pixelConv(4), 0, 2 * Math.PI);
          ctx.fill();
          ctx.fillStyle = foreColStr;
          ctx.fillText(axisItem.label, posX + pixelConv(10), posY - pixelConv(5));
          posX += itemSize;
        }
      }
      handled = true;
    }

    if (legendItem.type == LEGEND_ITEM_TYPES.PointSize) {
      const legendItemSize = legendItem as TpLegendSize;
      handled = true;
      if (legendItemSize.customTitle) {
        const txt = `${legendItemSize.customTitle}: Point size`;
        addString(txt);
      } else {
        if (legendItemSize.sizeColumnInfo) {
          const txt = `${legendItemSize.sizeColumnInfo.name}: Point size`;
          addString(txt);
        }
      }
    }

    if (legendItem.type == LEGEND_ITEM_TYPES.NamedColorSet) {
      for (const item of legendItem.items) {
        const itemSize = ctx.measureText(item.name).width + pixelConv(17);
        if (posX + itemSize > renderContext.areaXMax) {
          posX = renderContext.areaXMin;
          posY += lineH;
        }
        ctx.fillStyle = item.color.toCanvasString();
        ctx.beginPath();
        ctx.arc(posX + pixelConv(4), posY, pixelConv(4), 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = foreColStr;
        ctx.fillStyle = foreColStr;
        ctx.fillText(item.name, posX + pixelConv(10), posY - pixelConv(5));
        posX += itemSize;
      }
      handled = true;
    }

    if (!handled) throw `Invalid legend item: ${legendItem.type}`;
  }
}
